import { createApi } from '@reduxjs/toolkit/query/react';
import { createChatSessionDispatch, resetChatState, updateCurrentSocketObject, updateSocketConnectionError, updateSocketLoading, updateUserMessage, setShowInactivityModal } from './chatAdvisorSlice.slice';
import { io, Socket } from 'socket.io-client';
import { Dispatch } from 'redux';
import { Advisor } from '../advisor/advisortypes';
import { isStreamingAdvisor } from './chatAdvisorStreamingService';

interface Event<Data> {
  name: string;
  data: Data;
}

interface Options {
  url: string;
  event: Event<any>;
  token?: string;
  advisorId?: string; // Keep for backwards compatibility
  advisor?: Advisor; // Add advisor object
  sessionId?: string;
}

interface Meta {
  confidence_score?: number;
  source_ids?: SourceId[];
  session_id?: string;
  metadata?: Metadata[];
  top_k?: number;
  rephrased_query?: string;
  message_id?: string;
}

interface Metadata {
  Displacement?: string;
}

interface SourceId {
  source_id: string;
  page_labels: string[];
}

interface Displacement {
  Displacement?: string;
}

interface AdvisorUIMessage {
  isAdvisor: boolean;
  isUser: boolean;
  message: string;
  metaData: Meta;
  isFeedbackAllowed: boolean;
  feedback: string;
  displacement: Displacement[];
  rephrased_query: string;
  comment: string;
  isStreaming?: boolean; // Flag to indicate if this is a streaming message
  explainanswer: {
    answer: string,
    metaData: any,
    rephrased_query: string,
    reranked_context: any,
    retrieved_context: any
  };
  aiResponseReceivedTime: string;
}
let socket: Socket | null = null;
let connected: Promise<void> | null = null;
let listener: any;
export const connectSocket = (url: string, dispatch?: Dispatch, token?: string, isReconnect = false) => {
  if (socket) return;
  if (url) {
    socket = io(url, {
      reconnection: false,
      autoConnect: true,
      auth: {
        token: `Bearer ${token}`
      },
    });
    connected = new Promise<void>((resolve, reject) => {
      socket?.on('connect', () => {
        resolve();
        const socketPayload = {
          id: socket?.id,
          connected: socket?.connected,
        }
        dispatch && dispatch(updateCurrentSocketObject(socketPayload));
        dispatch && dispatch(updateSocketLoading(false));
        dispatch && dispatch(updateSocketConnectionError(null))
        if (isReconnect) {
          socket?.on('bot_uttered', listener);
        }
      });
      socket?.on('connect_error', (error: any) => {
        dispatch && dispatch(updateSocketLoading(false));
        dispatch && dispatch(resetChatState());
        dispatch && cleanupSocket(dispatch);
        const serializableError = {
          message: error.message,
          stack: error.stack,
        };
        dispatch && dispatch(updateSocketConnectionError(serializableError));
      });

      socket?.on('disconnect', () => {
        console.log('WebSocket disconnected');
        dispatch && dispatch(resetChatState());
        dispatch && dispatch(updateSocketConnectionError(null))
      });
    });
  }
};
export const disconnectSocket = (dispatch: Dispatch) => {
  if (socket) {
    if (listener && typeof listener === 'object') {
      Object.keys(listener).forEach((eventName) => {
        socket?.off(eventName, listener[eventName]);
      });
    }
    socket.disconnect();
    socket = null;
    connected = null;
    dispatch(chatAdvisorApi.util.resetApiState());
    cleanupSocket(dispatch);

  }
};
const cleanupSocket = (dispatch: Dispatch) => {
  if (socket) {
    socket.removeAllListeners(); // Remove all listeners to prevent memory leaks or unwanted behavior
    socket = null;
    connected = null;
    // Reset the state or show connection failed message
    // For example, update the state to indicate the connection failed
    dispatch && dispatch(updateSocketLoading(false));
    // Optionally, reset the API state or indicate a connection error in your UI
  }
};
export const chatAdvisorApi = createApi({
  reducerPath: 'chatAdvisorApi',
  tagTypes: ['Event'],
  async baseQuery(options: Options) {
    if (!socket) {
      connectSocket(options.url);
    }

    try {
      // Await the connection, which will reject if there's an error.
      await connected;
      socket?.emit(options.event.name, options.event.data);
      return { data: options.event };
    } catch (error: any) {
      // Return a standard error object that RTK Query will pick up.
      return { error: { message: error.message || 'Socket connection error' } };
    }
  },
  endpoints: (build) => ({

    sendEvent: build.mutation<Event<any>, Options>({
      query(options) {
        return options;
      },
      invalidatesTags: (result, error, { event }) => [{ type: 'Event', id: event.name }],
    }),
    events: build.query<{ value: number }[], Options>({
      queryFn() {
        return { data: [] };
      },
      keepUnusedDataFor: 0,
      providesTags: (result, error, { event }) => [{ type: 'Event', id: event.name }],
      async onCacheEntryAdded(
        { url, event, token, advisorId, advisor, sessionId },
        { cacheEntryRemoved, updateCachedData, cacheDataLoaded, dispatch }
      ) {
        const sessionDispatch = sessionId ? createChatSessionDispatch(dispatch, sessionId) : dispatch;
        // Note: For streaming advisors, we don't use WebSockets anymore
        // That logic is handled in the chatAdvisorStreamingService.ts file
        // This WebSocket handler is kept for backwards compatibility with non-streaming advisors
        
        if (isStreamingAdvisor(advisor)) {
          // Skip WebSocket setup for streaming advisors
          await cacheEntryRemoved;
          return;
        }

        if (!socket) {
          connectSocket(url, sessionDispatch, token);
        }

        await cacheDataLoaded;
        await connected;

        listener = (message: any) => {
          // Handle inactivity message
          if (message?.text === 'action_session_end') {
            console.log('Session ended');
            sessionDispatch(setShowInactivityModal(true));
            return;
          }
          
          try {
            // Regular non-streaming message handling
            const advisorUIMessage: Partial<AdvisorUIMessage> = {
              isAdvisor: true,
              isUser: false,
            }
            
            try {
              const parsedResponse = JSON.parse(message.text);
              
              advisorUIMessage.message = parsedResponse.predictions[0].answer
              advisorUIMessage.metaData = parsedResponse.predictions[0].meta || []
              advisorUIMessage.isFeedbackAllowed = parsedResponse.predictions[0].meta ? true : false
              advisorUIMessage.rephrased_query = parsedResponse?.predictions[0]?.meta[5]?.rephrased_query
              advisorUIMessage.displacement = parsedResponse?.predictions[0]?.meta[3]?.metadata || []
              advisorUIMessage.comment = ''
              advisorUIMessage.explainanswer = {
                answer: parsedResponse?.predictions[0]?.answer,
                rephrased_query: parsedResponse?.predictions[0]?.meta[5]?.rephrased_query,
                retrieved_context: parsedResponse?.predictions[0]?.retrived_context?.source_ids,
                reranked_context: parsedResponse?.predictions[0].meta[1]?.source_ids,
                metaData: parsedResponse?.predictions[0]?.meta[3]?.metadata || [],
              }
              advisorUIMessage.aiResponseReceivedTime = Date.now().toString();
            } catch (e) {
              advisorUIMessage.message = message.text;
              advisorUIMessage.aiResponseReceivedTime = Date.now().toString();
              console.log('Error parsing message', e);
            }
            
            sessionDispatch(updateUserMessage(advisorUIMessage));
            sessionDispatch(updateSocketLoading(false));
            updateCachedData((currentCacheData) => {
              currentCacheData.push(message);
            });
          } catch (e) {
            console.error('Error processing message', e);
          }
        };

        socket?.on(event.name, listener);
        await cacheEntryRemoved;
        socket?.off(event.name, listener);

        // Cleanup when the cache entry is removed
        if (socket?.listeners(event.name).length === 0) {
          console.log('No more listeners for event:');
          socket.disconnect();
          socket = null;
          connected = null;
        }
      },
    }),
  }),
});

export const { useSendEventMutation, useEventsQuery } = chatAdvisorApi;
